import { getCall, postCall, putCall, deleteCall, downloadCall } from "./api";

/**
 * A service, minus the plumbing.
 *
 * Every service file was rebuilding the same request object by hand — 139 of
 * them, alongside 111 written inline, so the folder had two spellings of one
 * idea and four lines of ceremony per call:
 *
 *     export const getDepartments = async () => {
 *       const request = { path: `/department/organization` };
 *       return await getCall(request);
 *     };
 *
 * With a resource bound to its base path that is one line, and the thing worth
 * reading — the URL and what it takes — is all that is left:
 *
 *     const api = resource("/department");
 *     export const getDepartments = () => api.get("/organization");
 *
 * Deliberately thin. It builds a path and forwards to the same api.js calls
 * everything already used; it does not retry, cache, unwrap, or normalise
 * errors. Callers keep reading `{ success, data, message }` exactly as before,
 * because a change of shape here would be a change to every screen.
 */

// Drop empty values rather than sending `?department=undefined`, which the
// server would read as the string "undefined" and filter on.
export const toQuery = (params = {}) => {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return;
    qs.set(key, value);
  });
  const query = qs.toString();
  return query ? `?${query}` : "";
};

const join = (base, path = "") => {
  const tail = String(path);
  if (!tail) return base;
  return tail.startsWith("/") ? `${base}${tail}` : `${base}/${tail}`;
};

/**
 * @param {string} base  path prefix, e.g. "/department"
 */
export const resource = (base) => ({
  get: (path, params) => getCall({ path: `${join(base, path)}${toQuery(params)}` }),
  post: (path, bodyParams = {}) => postCall({ path: join(base, path), bodyParams }),
  put: (path, bodyParams = {}) => putCall({ path: join(base, path), bodyParams }),
  del: (path) => deleteCall({ path: join(base, path) }),
  download: (path, params) => downloadCall({ path: `${join(base, path)}${toQuery(params)}` }),
});

export default resource;
